import { Tooltip as GpsTooltip } from 'react-leaflet';
import { gpsWarning } from '../../utils/gps-warning';
import { countSnapshotStates, snapshotMachineState } from '../../utils/dashboard-machine-state';
import { dateInputValue, isHistoricalRange, dashboardReferenceTime } from '../../utils/dashboard-range';
import React, { useState, useEffect, useLayoutEffect, useRef, useContext } from 'react';
import { GlobalContext } from 'context/userContext';
import { DrawerHeader } from '@components/drawer-header';

import PieDistribuionEquipament from '@components/charts/pie_distribution_chart';
import BarDistributionChart from '@components/charts/bar_distribution_chart';
import LinePerTimeChart from '@components/charts/line_per_time_chart';
import HeartbeatTimelineChart, { TimelineRangeDataPoint } from '@components/charts/heartbeat_timeline_chart';

import { Grid, Typography, Box, Alert, Tooltip, IconButton, Stack } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import TableExplorer from '@components/table-explorer/table_explorer';
import { AxiosInstanceLongTimeOut, API_URL } from '@service/axios-instance';
import { buildBoundedDateRange } from '../../utils/date-range';
import { secondsSinceTelemetryTimestamp } from '../../utils/telemetry-time';
import { calculateMaintenanceDowntimeSeconds, isMachineInOpenMaintenance } from '../../utils/machine-maintenance';
import { getMachineIcon, STATE_COLORS } from '../../utils/machine-icon';


import {
  MapContainer, TileLayer, Marker, Popup, Polyline, ImageOverlay,
  LayersControl, LayerGroup, useMap, Polygon, useMapEvents
} from 'react-leaflet'
import L from 'leaflet';
import "leaflet/dist/leaflet.css"
import { HeatmapLayer } from 'react-leaflet-heatmap-layer-v3';
import { LatLngExpression, LatLngBoundsExpression, } from 'leaflet';
import { RotatedImageOverlay } from '@components/map/leaflet-image-overlay-rotated';
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});





// Custom image and bounds (your floorplan should be ~2x1 ratio for this example)
const imageUrl = 'https://vitruvius.com.br/media/images/magazines/grid_12/ca3d_inst47-08.jpg';  // Put this in your `public/` folder

// You must calibrate these bounds to match real GPS coordinates
const imageBounds: LatLngBoundsExpression = [
  [-23.5510, -46.6340], // Southwest (bottom-left)
  [-23.5500, -46.6320], // Northeast (top-right)
];




interface MapCenterChangerProps {
  center: LatLngExpression;
}

const MapCenterChanger = ({ center }: MapCenterChangerProps) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center);
  }, [center, map]);

  return null;
};

// points (timestamped coordinates)
type Position = {
  lat: number;
  lng: number;
  timestamp: string;
};




function CacheMapState() {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      localStorage.setItem('cachedMapState', JSON.stringify({ lat: center.lat, lng: center.lng, zoom }));
    }
  });
  return null;
}

function FitMapToMarkers({ markers }: { markers: { lat: number; lng: number }[] }) {
  const map = useMap();
  const hasFitRef = useRef(false);

  useEffect(() => {
    if (!markers || markers.length === 0) return;

    const validMarkers = markers.filter(
      (m) =>
        m.lat != null &&
        m.lng != null &&
        m.lat !== 0 &&
        m.lng !== 0 &&
        !Number.isNaN(m.lat) &&
        !Number.isNaN(m.lng)
    );
    if (validMarkers.length === 0) return;

    // só faz o ajuste na primeira vez (pra não brigar com o usuário)
    if (hasFitRef.current) return;

    // bounds básico com os marcadores
    const baseBounds = L.latLngBounds(
      validMarkers.map((m) => L.latLng(m.lat, m.lng))
    );

    // span em latitude e longitude
    const latSpan = baseBounds.getNorth() - baseBounds.getSouth();
    const lngSpan = baseBounds.getEast() - baseBounds.getWest();
    // extra de ~10% (se tiver só 1 ponto ou span muito pequeno, usa um mínimo)
    const extraLat = (latSpan || 0.0005) * 0.1;
    const extraLng = (lngSpan || 0.0005) * 0.1;

    // estende o bounds na vertical e horizontal
    const extendedBounds = L.latLngBounds(
      [baseBounds.getSouth() - extraLat, baseBounds.getWest() - extraLng],
      [baseBounds.getNorth() + extraLat, baseBounds.getEast() + extraLng]
    );

    // padding em pixels (pode deixar baixo, o extra vertical já cuida do resto)
    const size = map.getSize();
    const padding = L.point(size.x * 0.03, size.y * 0.03);

    map.fitBounds(extendedBounds, { padding });

    hasFitRef.current = true;
  }, [markers, map]);

  return null;
}

export function OperationDashboard({ hideUI = false }: { hideUI?: boolean }) {
  const { operation } = useContext(GlobalContext);
  const { startDate } = useContext(GlobalContext);
  const { endDate } = useContext(GlobalContext);
  const { setor } = useContext(GlobalContext);
  const theme = useTheme();
  const historical = isHistoricalRange(endDate);
  const referenceTime = dashboardReferenceTime(endDate);



  const [machineQtd, setMachineQtd] = React.useState("");
  const [numReserve, setNumReserve] = React.useState("");

  const [realTimeCounters, setRealTimeCounters] = React.useState({
    ligadas: 0,
    ligadaParada: 0,
    manutencao: 0
  });


  const [proxPreventiva, setProxPreventiva] = React.useState("");

  type DataPoint = { x: Date; y: number };
  type DataPiePoint = { label: string; y: number; color?: string; };
  const [eqTypeDataPiePoins, setEqTypeDataPiePoins] = React.useState<DataPiePoint[]>([]);

  const [disponibilidadeDataPiePoins, setDisponibilidadeDataPiePoins] = React.useState<DataPiePoint[]>([]);
  const [eventsDataPiePoins, setEventsDataPiePoins] = React.useState<DataPiePoint[]>([]);
  const [actualStatusDataPiePoins, setActualStatusDataPiePoins] = React.useState<DataPiePoint[]>([]);
  const [usoDataPiePoins, setUsoDataPiePoins] = React.useState<DataPiePoint[]>([]);
  const [usersMap, setUsersMap] = React.useState<Record<string, string>>({});

  const [mapCenter, setMapCenter] = React.useState<LatLngExpression>([-23.5525, -46.6150]);
  const [positions, setPositions] = React.useState<Position[]>([]);
  const [areas, setAreas] = React.useState<any[]>([]);

  React.useEffect(() => {
    if (!operation) return;
    const requestURL = operation ? "/geofences/get_geofences?db=" + operation : "/geofences/get_geofences";
    AxiosInstanceLongTimeOut.post(requestURL, {}, request_config)
      .then((res) => {
        const adapted = (res.data || []).map((r: any) => ({
          ...r,
          points: Array.isArray(r.points) ? r.points.map((p: any) => (Array.isArray(p) ? { lat: p[0], lng: p[1] } : p)) : []
        }));
        setAreas(adapted);
      })
      .catch((error) => console.log(error));
  }, [operation]);

  type PreventivasHistogram = Record<string, number>;

  const [preventivasHistogram, setPreventivasHistogram] = useState<PreventivasHistogram>({});

  interface Series {
    type: "line";
    showInLegend: boolean;
    name: string;
    xValueFormatString: string;
    dataPoints: DataPoint[];
  }

  const [eventsPerTimeDatapoins, setEventsPerTimeDatapoins] = React.useState<Series[]>([]);
  const [utilizacaoPerTimeDatapoins, setUtilizacaoPerTimeDatapoins] = React.useState<Series[]>([]);
  const [rawUtilizacaoDaysDict, setRawUtilizacaoDaysDict] = React.useState<{ days_dict: any, dateRange: Date[] }>({ days_dict: {}, dateRange: [] });




  const [equipmentsData, setEquipmentsData] = React.useState<any[] | null>(null);
  const [maintenancesData, setMaintenancesData] = React.useState<any[] | null>(null);
  const [heartbeatsData, setHeartbeatsData] = React.useState<any[] | null>(null);
  const [validGpsData, setValidGpsData] = React.useState<any[] | null>(null);

  const machineStateCounts = countSnapshotStates(equipmentsData || [], heartbeatsData || [], referenceTime);

  const timelineStartDate = dateInputValue(startDate);
  const timelineEndDate = dateInputValue(endDate);
  const [timelinePage, setTimelinePage] = React.useState(1);
  const [timelineFilter, setTimelineFilter] = React.useState('');
  const ITEMS_PER_PAGE = 25;

  const [timelineRangePoints, setTimelineRangePoints] = React.useState<TimelineRangeDataPoint[]>([]);
  const [approxLocations, setApproxLocations] = React.useState<Record<string, { lat: number, lng: number }>>({});
  const [aggregatedDataLoaded, setAggregatedDataLoaded] = React.useState(false);
  const [useMaintenanceModule, setUseMaintenanceModule] = React.useState(false);
  const [mapOverlay, setMapOverlay] = React.useState<any>(null);


  interface DayStats {
    ligado: number;
    parado: number;
  }

  const request_dict = {
    "start_date": startDate,
    "end_date": endDate,
  }
  const request_config = { withCredentials: true };

  function getDateRange(start?: Date, end?: Date): Date[] {
    return buildBoundedDateRange(start, end);
  }

  // Throttled trigger: fires immediately on the first SSE event, then ignores
  // all further events for 1 minute before allowing another refresh.
  const [sseRefreshTrigger, setSseRefreshTrigger] = React.useState(0);

  useEffect(() => {
    if (historical) return;
    const sse = new EventSource(API_URL + 'sse/stream');
    let cooldownActive = false;

    const throttledRefresh = (e: any) => {
      try {
        const data = JSON.parse(e.data);
        if (data.db && data.db !== operation) return;
      } catch (err) { }

      // If we're still within the 1-minute cooldown, ignore this event.
      if (cooldownActive) return;

      // Trigger refresh immediately and start the 1-minute cooldown.
      cooldownActive = true;
      setSseRefreshTrigger(c => c + 1);
      setTimeout(() => {
        cooldownActive = false;
      }, 60_000); // unlock after 1 minute
    };

    sse.addEventListener('equipaments', throttledRefresh);
    sse.addEventListener('heartbeat', throttledRefresh);

    return () => { sse.close(); };
  }, [operation, historical]);

  //-----------------------------------------------------
  //-----------------------------------------------------

  useEffect(() => {
    if (operation == null) return;

    AxiosInstanceLongTimeOut.get('/operations/current_config', { params: { db: operation }, withCredentials: true })
      .then((res: any) => {
        if (res.data && res.data.USE_MAINTENANCE_MODULE) {
          setUseMaintenanceModule(true);
        } else {
          setUseMaintenanceModule(false);
        }
        if (res.data && res.data.MAP_OVERLAY) {
          setMapOverlay(res.data.MAP_OVERLAY);
        } else {
          setMapOverlay(null);
        }
      })
      .catch((err: any) => console.log('Error fetching operation config:', err));

    const refreshData = () => {
      fetchEquipamentsData().then((eqData) => {
        if (!eqData) return;
        const validIds = eqData.map((e: any) => e.id);
        fetchHeartbeatData(validIds);
        fetchEventsData(validIds);
        fetchMaintanencesData(validIds);
      });
    };

    refreshData();
  }, [operation, startDate, endDate, setor, sseRefreshTrigger]);

  useEffect(() => {
    if (operation == null || !equipmentsData) return;

    const fetchTimelineData = () => {
      // Compute paginated equipment IDs
      let filteredEquipments = equipmentsData || [];
      if (setor && setor !== "TODOS") {
        filteredEquipments = filteredEquipments.filter((e: any) => e.setor === setor);
      }
      if (timelineFilter) {
        filteredEquipments = filteredEquipments.filter((e: any) => {
          const label = e.apelido ? `${e.id} - ${e.apelido}` : String(e.id);
          return label.toLowerCase().includes(timelineFilter.toLowerCase());
        });
      }

      const totalTimelinePages = Math.ceil(filteredEquipments.length / ITEMS_PER_PAGE) || 1;
      const paginatedEquipments = filteredEquipments.slice((timelinePage - 1) * ITEMS_PER_PAGE, timelinePage * ITEMS_PER_PAGE);
      const paginatedIds = paginatedEquipments.map((e: any) => e.id);

      if (paginatedIds.length === 0) {
        setTimelineRangePoints([]);
        return;
      }

      const raw_hb_request = {
        start_date: startDate,
        end_date: endDate,
        match_filters_dict: { "id": paginatedIds }
      };

      AxiosInstanceLongTimeOut.post("/heartbeats/get_state_transitions?db=" + operation, raw_hb_request, request_config)
        .then((response) => {
          const transitions = response.data || [];

          // Group by machine to assign rowIdx
          const transByMachine: Record<string, any[]> = {};
          transitions.forEach((t: any) => {
            if (!transByMachine[t.id]) transByMachine[t.id] = [];
            transByMachine[t.id].push(t);
          });

          const points: TimelineRangeDataPoint[] = [];
          let rowIdx = 1;

          Object.keys(transByMachine).sort().forEach((machineId) => {
            const machineTransitions = transByMachine[machineId];

            machineTransitions.forEach((t: any, index: number) => {
              let currentState = Number(t.state);

              if (currentState === 0) {
                const nextT = machineTransitions[index + 1];
                if (!nextT || Number(nextT.state) !== 1) {

                  //ATENTION HERE WE MADE A CONVERSION OF STATE! REVIEW IN FUTURE IF IT REALLY MAKE SENSE!!
                  currentState = 4; // Convert the rest to 'Operando' instead of skipping
                }
              }

              let color = '#9ca3af'; // gray (idle fallback)
              let stateLabel = 'Ocioso';

              if (currentState === 4) {
                color = STATE_COLORS["Operando"];
                stateLabel = 'Operando';
              } else if (currentState === 7) {
                color = STATE_COLORS["Manutenção"];
                stateLabel = 'Manutenção';
              } else if (currentState === 6) {
                color = STATE_COLORS["Atualizando"];
                stateLabel = 'Atualizando';
              } else if (currentState === 5) {
                color = STATE_COLORS["Bloqueado Remoto"];
                stateLabel = 'Bloqueado Remoto';
              } else if (currentState === 3) {
                color = STATE_COLORS["Bloqueada"];
                stateLabel = 'Bloqueada';
              } else if (currentState === 2) {
                color = STATE_COLORS["Checklist"];
                stateLabel = 'Checklist';
              } else if (currentState === 1) {
                color = STATE_COLORS["Login"];
                stateLabel = 'Login';
              } else if (currentState === 0) {
                color = STATE_COLORS["Desligado"];
                stateLabel = 'Desligado';
              }

              const startT = new Date(t.start + (t.start.endsWith('Z') ? '' : 'Z')).getTime();
              const endT = new Date(t.end + (t.end.endsWith('Z') ? '' : 'Z')).getTime();

              const eqInfo = equipmentsData?.find((e: any) => e.id === machineId);
              const displayName = eqInfo?.apelido ? `${machineId} - ${eqInfo.apelido}` : machineId;

              points.push({
                x: rowIdx,
                y: [startT, endT],
                color,
                label: displayName,
                toolTipContent: `<strong>${displayName}</strong><br/>Estado: ${stateLabel}`
              });
            });
            rowIdx++;
          });

          setTimelineRangePoints(points);
        })
        .catch((error) => console.log(error));
    };

    fetchTimelineData();
  }, [operation, timelineStartDate, timelineEndDate, equipmentsData, setor, timelinePage, timelineFilter]);

  useEffect(() => {
    if (!operation) return;
    AxiosInstanceLongTimeOut.post('/users-machine/get_users-machine?db=' + operation, {}, { withCredentials: true })
      .then((res: any) => {
        const map: Record<string, string> = {};
        if (Array.isArray(res.data)) {
          res.data.forEach((u: any) => {
            const userName = u.name || u.nome || u.username || String(u.login || "");

            if (u.login !== undefined && u.login !== null && u.login !== "") {
              const loginStr = u.login.toString().trim().toLowerCase();
              map[loginStr] = userName;
              map[u.login.toString()] = userName;
              if (loginStr !== "" && !isNaN(Number(u.login))) {
                map[Number(u.login).toString()] = userName;
              }
            }
          });
        }
        setUsersMap(map);
      })
      .catch(console.error);
  }, [operation]);

  //----------------------data processing-----------------------------



  React.useEffect(() => {
    if (rawUtilizacaoDaysDict.dateRange && rawUtilizacaoDaysDict.dateRange.length > 0 && equipmentsData) {
      const dataserieLigado: DataPoint[] = [];
      const dataserieLigadoParado: DataPoint[] = [];

      const minutes_per_hour = 60;
      let m_count = equipmentsData.length > 0 ? equipmentsData.length : 1;
      if (heartbeatsData && heartbeatsData.length > 0) {
        m_count = heartbeatsData.length;
      }

      rawUtilizacaoDaysDict.dateRange.forEach(day => {
        const dateStr = day.toISOString().split("T")[0];

        dataserieLigado.push({ "x": day, "y": (rawUtilizacaoDaysDict.days_dict[dateStr]["ligado"] / minutes_per_hour) / m_count });
        dataserieLigadoParado.push({ "x": day, "y": (rawUtilizacaoDaysDict.days_dict[dateStr]["parado"] / minutes_per_hour) / m_count });
      });

      const UtilizacaoDiaDataPoints: Series[] = [];
      UtilizacaoDiaDataPoints.push({
        type: "line",
        showInLegend: true,
        name: "Ligada",
        xValueFormatString: "MMM DD",
        dataPoints: dataserieLigado,
      });

      UtilizacaoDiaDataPoints.push({
        type: "line",
        showInLegend: true,
        name: "Osciosa",
        xValueFormatString: "MMM DD",
        dataPoints: dataserieLigadoParado,
      });

      setUtilizacaoPerTimeDatapoins(UtilizacaoDiaDataPoints);
    }
  }, [rawUtilizacaoDaysDict, equipmentsData, heartbeatsData]);

  // React to data changes to compute metrics
  React.useEffect(() => {
    if (equipmentsData && maintenancesData) {
      const start = new Date(startDate || new Date(new Date().setMonth(new Date().getMonth() - 3)));
      let end = new Date();
      if (endDate) end = new Date(endDate);
      const total_time_diffs = (end.getTime() / 1000 - start.getTime() / 1000) * equipmentsData.length;

      let total_maintanence_diffs = calculateMaintenanceDowntimeSeconds(maintenancesData, start, end);
      let op_diffs = total_time_diffs - total_maintanence_diffs;
      if (Number.isNaN(op_diffs) || Number.isNaN(total_maintanence_diffs)) {
        op_diffs = 1;
        total_maintanence_diffs = 0;
      }
      setDisponibilidadeDataPiePoins([{ "label": "Operacional", y: Math.max(0, op_diffs), color: STATE_COLORS["Operando"] || '#4CAF50' }, { "label": "Manutenção", y: total_maintanence_diffs, color: STATE_COLORS["Manutenção"] || '#7b102a' }]);
    }
  }, [equipmentsData, maintenancesData, startDate, endDate]);

  React.useEffect(() => {
    if (historical) { setApproxLocations({}); return; }
    if (equipmentsData && validGpsData) {
      const missingGpsIds: string[] = [];
      equipmentsData.forEach((equipment: any) => {
        const id = equipment.id;
        const lastgpshb = validGpsData.find((h: any) => h.id === id);
        if (!lastgpshb) {
          missingGpsIds.push(id);
          return;
        }
        const lat = lastgpshb.lat ?? lastgpshb.latitude;
        const lng = lastgpshb.lng ?? lastgpshb.longitude;
        if (lat === 0 || lng === 0 || lat == null || lng == null) {
          missingGpsIds.push(id);
        }
      });

      if (missingGpsIds.length > 0) {
        AxiosInstanceLongTimeOut.post('/equipaments/approx_locations_batch?db=' + operation, { ids: missingGpsIds }, request_config)
          .then(res => {
            if (res.data) {
              setApproxLocations(res.data);
            }
          })
          .catch(err => console.error("Error fetching approx locations:", err));
      }
    }
  }, [equipmentsData, validGpsData, operation]);

  React.useEffect(() => {
    if (equipmentsData) {
      setMachineQtd(String(equipmentsData.length));
      let num_reserve = 0;
      const sum_by_category: any = { "tipo": {} };
      equipmentsData.forEach((eq: any) => {
        if (eq["reserva"] == 1) num_reserve++;
        const cat = "tipo";
        if (!sum_by_category[cat][eq[cat]]) sum_by_category[cat][eq[cat]] = 0;
        sum_by_category[cat][eq[cat]]++;
      });
      setNumReserve(String(num_reserve));

      const datapoints: any = [];
      Object.keys(sum_by_category["tipo"]).forEach(label => datapoints.push({ y: sum_by_category["tipo"][label], label: label }));
      setEqTypeDataPiePoins(datapoints);
    }
  }, [equipmentsData]);

  React.useEffect(() => {
    if (equipmentsData && heartbeatsData) {
      const horimetroPerMachine: any = {};
      const equipmentsMap: any = {};
      equipmentsData.forEach((eq: any) => { equipmentsMap[eq.id] = eq; });

      const states_counter: any = {
        "Desligado": 0, "Login": 0, "Checklist": 0, "Bloqueada": 0,
        "Operando": 0, "Bloqueado Remoto": 0, "Atualizando": 0, "Manutenção": 0,
      };
      let count_ligadas = 0;
      let count_ligada_parada = 0;
      let count_manutencao = 0;

      const isPointInPolygon = (lat: number, lng: number, polygon: { lat: number, lng: number }[]) => {
        const x = lat, y = lng;
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const xi = polygon[i].lat, yi = polygon[i].lng;
          const xj = polygon[j].lat, yj = polygon[j].lng;
          const intersect = ((yi > y) != (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
          if (intersect) inside = !inside;
        }
        return inside;
      };

      heartbeatsData.forEach((hb: any) => {
        const id = hb["id"];
        if (!equipmentsMap[id]) return;

        let inMaintenanceArea = false;
        const lastgpshb = validGpsData?.find((g: any) => g.id === id);
        const lat = hb.lat ?? hb.gps?.lat ?? lastgpshb?.lat ?? lastgpshb?.latitude ?? approxLocations[id]?.lat;
        const lng = hb.lng ?? hb.gps?.lng ?? lastgpshb?.lng ?? lastgpshb?.longitude ?? approxLocations[id]?.lng;

        if (lat && lng) {
          areas.filter(a => a.isMaintenanceArea && a.status !== 'Desabilitado' && a.points.length >= 3).forEach(a => {
            if (isPointInPolygon(lat, lng, a.points)) {
              inMaintenanceArea = true;
            }
          });
        }

        const isReserva = equipmentsMap[id]?.reserva == 1 || equipmentsMap[id]?.reserva === true;

        if (maintenancesData && isMachineInOpenMaintenance(maintenancesData, id)) {
          states_counter["Manutenção"]++;
          count_manutencao++;
        } else if (inMaintenanceArea && !isReserva) {
          states_counter["Manutenção"]++;
          count_manutencao++;
          horimetroPerMachine[id] = hb["horimetro"] / 60; // Keep calculating horimetro
        } else {
          horimetroPerMachine[id] = hb["horimetro"] / 60;
          const diffSeconds = secondsSinceTelemetryTimestamp(hb["stamp"], referenceTime);
          if (diffSeconds === null || diffSeconds > 60 * 15) {
            states_counter["Desligado"]++;
          } else {
            const state_name = state_i_to_name(hb['state']);
            if (states_counter[state_name] === undefined) states_counter[state_name] = 0;
            states_counter[state_name]++;
            if (hb['state'] === 7) {
              count_manutencao++;
            }
            if (hb['state'] === 4 || hb['state'] === 1 || hb['state'] === 2 || hb['state'] === 6) {
              count_ligadas++;
              if ((hb['vx'] || 0) < 0.1) count_ligada_parada++;
            }
          }
        }
      });

      const n_machines = equipmentsData.length;
      if (n_machines > 0) {
        const datapoints: any = [];
        Object.entries(countSnapshotStates(equipmentsData, heartbeatsData, referenceTime)).forEach(([label, count]) => datapoints.push({ y: count, label, color: STATE_COLORS[label] }));
        setActualStatusDataPiePoins(datapoints);
      }

      setRealTimeCounters({ ligadas: count_ligadas, ligadaParada: count_ligada_parada, manutencao: count_manutencao });

      if (Object.keys(horimetroPerMachine).length > 0) {
        const preventivas_histogram: any = { "vencidas": 0, "0s": 0, "1s": 0, "2s": 0, "3s": 0, "+4s": 0 };
        equipmentsData.forEach((eq: any) => {
          if (horimetroPerMachine[eq.id] !== undefined) {
            let previsao_dias = Math.floor((eq["proxima_preventiva"] - horimetroPerMachine[eq.id]) / 24);
            if (eq["horas_contratadas"] != undefined) {
              previsao_dias = Math.floor((eq["proxima_preventiva"] - horimetroPerMachine[eq.id]) / eq["horas_contratadas"]);
            }
            const semanas = Math.floor(previsao_dias / 7);
            if (semanas < 0) preventivas_histogram["vencidas"]++;
            else if (semanas === 0) preventivas_histogram["0s"]++;
            else if (semanas === 1) preventivas_histogram["1s"]++;
            else if (semanas === 2) preventivas_histogram["2s"]++;
            else if (semanas === 3) preventivas_histogram["3s"]++;
            else preventivas_histogram["+4s"]++;
          }
        });
        setPreventivasHistogram(preventivas_histogram);
      }
    }
  }, [equipmentsData, heartbeatsData, maintenancesData, validGpsData, approxLocations, areas]);




  function state_i_to_name(state_i: number) {
    switch (state_i) {
      case 0:
        return "Desligado";
        break;
      case 1:
        return "Login";
        break;
      case 2:
        return "Checklist";
        break;
      case 3:
        return "Bloqueada";
        break;
      case 4:
        return "Operando";
        break;
      case 5:
        return "Bloqueado Remoto";
        break;
      case 6:
        return "Atualizando";
        break;
      case 7:
        return "Manutenção";
        break;
      default:
        console.log("estado nao conhecido!");
    }
    return "estado nao conhecido!";
  }




  //----------------------------------------------------------------------------

  const fetchMaintanencesData = async (validIds?: string[]) => {
    const tableName = "maintenances";
    const requestURL = "/" + tableName + "/get_" + tableName + "?db=" + operation;
    const reqDict: any = { ...request_dict, start_date: undefined };
    if (validIds && validIds.length > 0) {
      reqDict.match_filters_dict = { "id": validIds };
    }
    AxiosInstanceLongTimeOut.post(requestURL, reqDict, request_config)
      .then((response) => {
        setMaintenancesData((response.data || []).filter((ticket: any) => {
          const closed = ticket.stamp_end || ticket.closed_at;
          return !closed || new Date(closed) >= new Date(startDate);
        }));
      })
      .catch((error) => console.log(error));
  };

  const fetchEquipamentsData = async () => {
    const tableName = "equipaments";
    const requestURL = "/" + tableName + "/get_" + tableName + "?db=" + operation;
    return AxiosInstanceLongTimeOut.post(requestURL, {}, request_config)
      .then((response) => {
        let data = response.data || [];
        if (setor && setor !== "TODOS") {
          data = data.filter((eq: any) => eq.setor === setor);
        }
        setEquipmentsData(data);
        return data;
      })
      .catch((error) => console.log(error));
  };







  const fetchHeartbeatData = async (validIds?: string[]) => {
    const tableName = "heartbeats";

    const last_hb_request_dict = {
      start_date: startDate,
      end_date: endDate,
      match_filters_dict: validIds && validIds.length > 0 ? { "id": validIds } : undefined
    }

    const requestURL_Last = "/" + tableName + "/get_last_" + tableName + "_per_id?db=" + operation

    AxiosInstanceLongTimeOut.post(requestURL_Last, last_hb_request_dict, request_config)
      .then((response) => setHeartbeatsData(response.data || []))
      .catch((error) => console.log(error));

    const valid_gps_request_dict = { ...last_hb_request_dict, only_valid_gps: true };
    AxiosInstanceLongTimeOut.post(requestURL_Last, valid_gps_request_dict, request_config)
      .then((validGpsResponse) => setValidGpsData(validGpsResponse.data || []))
      .catch(err => console.error("Error fetching valid GPS", err));

    // Timeline chart fetching logic extracted to separate useEffect


    //----------------------------utilizacao-------------------------------------
    const requestURL_aggregated = "/" + tableName + "/get_aggregated_" + tableName + "?db=" + operation
    //UTILIZACAO PIE
    const hb_agregated_request_dict = {
      "collum_name_agregator": "vx",
      "date_unit_aggregator": "day",
      "start_date": startDate,
      "end_date": endDate,
      "match_filters_dict": validIds && validIds.length > 0 ? { "id": validIds } : undefined
    }

    console.log("REQUEST_AG_HB ", tableName, " requesting ", requestURL_aggregated);
    AxiosInstanceLongTimeOut.post(requestURL_aggregated, hb_agregated_request_dict, request_config)
      .then((response) => {

        let SstartDate;
        if (startDate == undefined) {
          try {
            // 1. Get the array of keys
            // 2. Grab the first key [0]
            // 3. Wrap it in new Date() to convert the string to a Date object
            const targetData = response.data["0.0"] || response.data["0"];
            const extractedKey = Object.keys(targetData[0])[0];
            SstartDate = new Date(extractedKey);
            console.log("HEARTBEAT FIRST DATE", SstartDate);

          } catch (error) {
            console.log("Could not read date from response. Defaulting to 3 months ago.");

            // Calculate the date exactly 3 months ago from today
            SstartDate = new Date(); // Gets right now
            SstartDate.setMonth(SstartDate.getMonth() - 3);
          }
        }
        else {
          SstartDate = startDate;
        }

        console.log("SstartDate", SstartDate)

        const dateRange = getDateRange(SstartDate, endDate);

        const days_dict: Record<string, DayStats> = {};
        dateRange.forEach(day => {
          const dateStr = day.toISOString().split("T")[0];
          days_dict[dateStr] = { "ligado": 0, "parado": 0 };
        });

        //console.log("full daydict",days_dict);

        let full_sum_parado = 0;
        let full_sum_movimento = 0;

        // Iterate over all returned keys (vx values)
        Object.keys(response.data).forEach((key) => {
          // Convert key to float (handles "1", "1.0", "0", "0.0", etc.)
          const vxValue = parseFloat(key);
          const records = response.data[key];

          if (isNaN(vxValue)) return;

          // Check if stopped (vx < 0.1) or moving
          if (Math.abs(vxValue) < 0.1) {
            // Ligado Parado (Stopped)
            records?.forEach((val: Record<string, number>) => {
              const date = Object.keys(val)[0];

              // Ensure existence (though initialized above)
              if (!days_dict[date]) {
                days_dict[date] = { ligado: 0, parado: 0 };
              }

              full_sum_parado += val[date];
              days_dict[date]["ligado"] += val[date];
              days_dict[date]["parado"] += val[date];
            });

          } else {
            // Ligado em Movimento (Moving)
            records?.forEach((val: Record<string, number>) => {
              const date = Object.keys(val)[0];

              if (!days_dict[date]) {
                days_dict[date] = { ligado: 0, parado: 0 };
              }

              full_sum_movimento += val[date];
              days_dict[date]["ligado"] += val[date];
            });
          }
        });

        //console.log(days_dict);

        const total_minutes = dateRange.length * 24 * 60 * Object.keys(response.data).length; // Need an approximation of machines?
        // Wait, response.data has keys as `vx`. We don't know the exact number of machines from this aggregated endpoint.
        // But since we want raw values, let's just pass the total minutes as `full_sum_movimento` and `full_sum_parado`.
        // Actually, if we don't know the number of machines, `desligado` cannot be accurately calculated.
        // Let's approximate it by taking the max potential minutes. If `num_machines` isn't available, we'll try to estimate it.
        // A better approach is to use `total_minutes` calculated using the total number of machines.
        // Wait, for now, let's just use raw values!
        setUsoDataPiePoins(
          [
            { "label": "desligado", "y": Math.max(0, total_minutes - full_sum_movimento - full_sum_parado), color: STATE_COLORS["Desligado"] || '#2196F3' },
            { "label": "movimento", "y": full_sum_movimento, color: STATE_COLORS["Operando"] || '#4CAF50' },
            { "label": "ligado parado", "y": full_sum_parado, color: STATE_COLORS["Ocioso"] || '#9ca3af' },
          ]
        );



        setRawUtilizacaoDaysDict({ days_dict: days_dict, dateRange: dateRange });

      })
      .catch((error) => console.log(error));
  };

  const translateEventLabel = (k: string): string => {
    if (k === "`ogin" || k === "login") return "Login";
    if (k === "boot_anomaly") return "Erro Boot";
    if (k === "crash" || k === "batida") return "Impacto";
    if (k === "crash_fwd") return "Impacto (Frente)";
    if (k === "crash_rev") return "Impacto (Ré)";
    if (k === "hole" || k === "buraco") return "Buraco";
    return k;
  };


  const fetchEventsData = async (validIds?: string[]) => {
    const tableName = "events";

    const requestURL = "/" + tableName + "/get_aggregated_" + tableName + "?db=" + operation

    //EVENTS PIE
    const ev_request_dict = {
      "collum_name_agregator": "type",
      "start_date": startDate,
      "end_date": endDate,
      "match_filters_dict": validIds && validIds.length > 0 ? { "id": validIds } : undefined
    }

    console.log("REQUEST_AG_EVENTS ", tableName, " requesting ", requestURL);
    AxiosInstanceLongTimeOut.post(requestURL, ev_request_dict, request_config)
      .then((response) => {
        const datapoints: { label: string; y: number }[] = [];

        //const total = response.data["total"]-response.data["login"];
        const total = response.data["total"];


        Object.keys(response.data).forEach(key => {
          const labelName = translateEventLabel(key);
          if (!["total"].includes(labelName)) {
            datapoints.push({ "label": labelName, "y": response.data[key] });
          }
        });
        setEventsDataPiePoins(datapoints);

      })
      .catch((error) => {
        console.log(error)
      })

    //EVENTS PER DAY CHART
    const ev_request_dict2 = {
      "collum_name_agregator": "type",
      "date_unit_aggregator": "day",
      "start_date": startDate,
      "end_date": endDate,
      "match_filters_dict": validIds && validIds.length > 0 ? { "id": validIds } : undefined
    }

    console.log("REQUEST_AG_EVENTS ", tableName, " requesting ", requestURL);
    AxiosInstanceLongTimeOut.post(requestURL, ev_request_dict2, request_config)
      .then((response) => {
        //console.log(response.data);

        const eventsDataPoints: Series[] = [];

        Object.keys(response.data).forEach(key => {
          const labelName = translateEventLabel(key);
          const dataserie: DataPoint[] = [];

          response.data[key].forEach((val: Record<string, number>) => {
            const date = Object.keys(val)[0];
            dataserie.push({ x: new Date(date), y: val[date] })
          });

          eventsDataPoints.push({
            type: "line",
            showInLegend: true,
            name: labelName,
            xValueFormatString: "MMM DD",
            dataPoints: dataserie,
          })

        })

        setEventsPerTimeDatapoins(eventsDataPoints);
      })
      .catch((error) => {
        console.log(error)
      })
  };





  //-----------------------------------------------------
  //-----------------------------------------------------


  return (
    <DrawerHeader
      title={"Dashboard Operação"}
      hideUI={hideUI}
    >

      <Grid container spacing={hideUI ? 0 : 2} sx={hideUI ? { p: 2, width: '100%', boxSizing: 'border-box', height: '100vh', overflow: 'hidden' } : { mt: 0, ml: 0 }} direction="column" wrap="nowrap">
        {!hideUI && (
          <Grid item xs={12}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2.5, md: 3 },
                bgcolor: 'background.paper',
                borderLeft: `6px solid ${theme.palette.primary.main}`,
                borderRadius: 2,
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={3}>
                <Box>
                  <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Visão Geral</Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    Acompanhe o status das máquinas no fim do período selecionado. Identifique gargalos, tempo ocioso e disponibilidade geral para otimizar o fluxo de trabalho diário.
                  </Typography>
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  bgcolor: 'background.default',
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  maxWidth: 400
                }}>
                  <InfoOutlinedIcon color="primary" sx={{ fontSize: 30 }} />
                  <Typography variant="body2" color="text.secondary" lineHeight={1.3}>
                    Disponível no <b>Modo TV</b> através do link:<br />
                    <a href={`${window.location.origin}/tv`} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none', fontWeight: 800 }}>
                      {window.location.origin}/tv
                    </a>
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} sx={hideUI ? { mb: 2 } : {}}>

          <Grid container spacing={2} direction="row">

            <Grid item xs={12} sm={4} md={2}>
              <Paper elevation={5} style={{ padding: "10px 20px 10px 20px", width: "100%", height: "100%" }}><h3>Operação: {operation}</h3></Paper>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Paper elevation={5} style={{ padding: "10px 20px 10px 20px", width: "100%", height: "100%" }}><h3>Equipamento: {machineQtd}</h3></Paper>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Paper elevation={5} style={{ padding: "10px 20px 10px 20px", width: "100%", height: "100%" }}><h3>Reservas: {numReserve}</h3></Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={5} sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Máquinas por estado</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
                  {Object.entries(machineStateCounts).map(([state, count]) => (
                    <Box key={state} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STATE_COLORS[state] || '#9ca3af', flexShrink: 0 }} />
                      <Typography variant="body2">{state}: <strong>{count}</strong></Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>


          </Grid>
        </Grid>



        <Grid item xs={12}>
          <Paper elevation={5} style={{ padding: "10px 20px 10px 20px", width: "100%", height: "100%", position: "relative" }}>

            {historical && <Alert severity="warning" sx={{ mb: 1 }}>
              Este mapa não será atualizado automaticamente: é uma visualização de {new Date(endDate).toLocaleDateString('pt-BR')}.
            </Alert>}
            <MapContainer center={mapCenter} zoom={18} style={{ height: hideUI ? 'calc(100vh - 165px)' : '600px', width: '100%' }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <CacheMapState />

              <MapCenterChanger center={mapCenter} />

              <LayersControl position="topright">

                <LayersControl.Overlay checked name="Mapa detalhado (Planta)">
                  {mapOverlay?.enabled && mapOverlay?.imageUrl && mapOverlay?.topleft && mapOverlay?.topright && mapOverlay?.bottomleft ? (
                    <RotatedImageOverlay
                      url={mapOverlay.imageUrl}
                      topleft={mapOverlay.topleft}
                      topright={mapOverlay.topright}
                      bottomleft={mapOverlay.bottomleft}
                      opacity={mapOverlay.opacity ?? 0.75}
                    />
                  ) : (
                    <ImageOverlay
                      url={imageUrl}
                      bounds={imageBounds}
                      opacity={0.75}
                      className="rotated-overlay"
                    />
                  )}
                </LayersControl.Overlay>

                <LayersControl.Overlay checked name="Áreas de Manutenção">
                  <LayerGroup>
                    {areas.filter((a: any) => a.status !== 'Desabilitado' && a.points.length >= 3).map((a: any) => (
                      <Polygon
                        key={a.id}
                        positions={a.points}
                        pathOptions={{ color: a.isMaintenanceArea ? 'orange' : 'blue', weight: 2, fillOpacity: 0.15 }}
                      >
                        <Popup>
                          <b>{a.name}</b><br />
                          {a.isMaintenanceArea && <i>Área de Manutenção<br /></i>}
                          {a.points.length} ponto(s)
                        </Popup>
                      </Polygon>
                    ))}
                  </LayerGroup>
                </LayersControl.Overlay>

                <LayersControl.Overlay checked name="Máquinas">
                  <LayerGroup>

                    {/* Marcadores por máquina usando último GPS válido */}
                    {equipmentsData && validGpsData && heartbeatsData && equipmentsData.map((equipment: any) => {
                      const id = equipment.id;
                      const lastgpshb = validGpsData.find((h: any) => h.id === id);

                      let lat = lastgpshb?.lat ?? lastgpshb?.latitude;
                      let lng = lastgpshb?.lng ?? lastgpshb?.longitude;
                      let isApprox = false;

                      if (lat === 0 || lng === 0 || lat == null || lng == null) {
                        if (approxLocations[id]) {
                          lat = approxLocations[id].lat;
                          lng = approxLocations[id].lng;
                          isApprox = true;
                        } else {
                          return null;
                        }
                      }

                      const lasthb = heartbeatsData.find((h: any) => h.id === id);
                      if (!lasthb) return null;

                      const state = snapshotMachineState(lasthb, referenceTime);

                      const tipo = equipment["tipo"] || "Contrabalançada";

                      return (
                        <Marker key={`machine-${id}`} position={[lat, lng]} icon={getMachineIcon(state, tipo)}>
                              {lastgpshb?.gps_imprecise && <GpsTooltip>{gpsWarning(lastgpshb, referenceTime)}</GpsTooltip>}
                          <Popup>
                            <div>
                              <b>Máquina:</b> {equipment.apelido ? `${id} - ${equipment.apelido}` : id}
                              <br />
                              <b>Tipo:</b> {tipo}
                              <hr />
                              <b>Setor:</b> {lasthb.setor}
                              <br />
                              <b>Reserva:</b> {equipment.reserva == 1 || equipment.reserva === true ? "Sim" : "Não"}
                              <br />
                              <b>Último Update:</b> {lasthb.stamp ? new Date(String(lasthb.stamp).includes('GMT') || String(lasthb.stamp).endsWith('Z') ? lasthb.stamp : lasthb.stamp + 'Z').toLocaleString() : "N/A"}
                              <br />
                              <b>Último GPS:</b> {lastgpshb?.stamp ? new Date(String(lastgpshb.stamp).includes('GMT') || String(lastgpshb.stamp).endsWith('Z') ? lastgpshb.stamp : lastgpshb.stamp + 'Z').toLocaleString() : "N/A"}
                              <br />
                              <b>Estado:</b> {state}
                              <br />
                              <b>Usuário:</b> {lasthb.user} {(() => {
                                const userKey = (lasthb.user !== undefined && lasthb.user !== null) ? lasthb.user.toString().trim().toLowerCase() : "";
                                const userNumKey = !isNaN(Number(lasthb.user)) ? Number(lasthb.user).toString() : "";
                                const foundName = usersMap[userKey] || usersMap[userNumKey] || usersMap[lasthb.user];
                                return (foundName && foundName !== String(lasthb.user)) ? `/ ${foundName}` : "";
                              })()}
                              <br />
                              {isApprox && (
                                <>
                                  <hr />
                                  <b style={{ color: 'orange' }}>⚠️ Localização aproximada por antena de celular</b>
                                </>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}


                  </LayerGroup>
                </LayersControl.Overlay>

              </LayersControl>

              {Object.keys(approxLocations).length > 0 && (
                <Box sx={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, width: '80%' }}>
                  <Alert severity="warning" sx={{ boxShadow: 3 }}>
                    Algumas máquinas estão sendo exibidas com <b>localização aproximada</b> baseada em antenas de celular, pois não possuem sinal de GPS no momento.
                  </Alert>
                </Box>
              )}

              <FitMapToMarkers
                markers={(equipmentsData || [])
                  .map((eq: any) => {
                    const id = eq.id;
                    const hb = validGpsData?.find((h: any) => h.id === id);
                    let lat = hb?.lat ?? hb?.latitude;
                    let lng = hb?.lng ?? hb?.longitude;
                    if (lat === 0 || lng === 0 || lat == null || lng == null) {
                      if (approxLocations[id]) {
                        lat = approxLocations[id].lat;
                        lng = approxLocations[id].lng;
                      }
                    }
                    return { lat, lng };
                  })
                  .filter((m: any) => m.lat != null && m.lng != null && m.lat !== 0 && m.lng !== 0)}
              />


            </MapContainer>

            <Box sx={{
              position: 'absolute',
              top: 30,
              right: 30,
              zIndex: 400,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(16, 26, 30, 0.92)' : 'rgba(240, 240, 240, 0.85)',
              border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.05)',
              padding: '10px 15px',
              borderRadius: '8px',
              boxShadow: theme.palette.mode === 'dark' ? '0 4px 16px rgba(0,0,0,0.5)' : '0 2px 4px rgba(0,0,0,0.2)',
              pointerEvents: 'none',
              backdropFilter: 'blur(4px)'
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.primary' }}>
                Estados
              </Typography>
              {Object.entries(STATE_COLORS).map(([state, color]) => (
                <Box key={state} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    backgroundColor: color,
                    mr: 1,
                    border: '1px solid rgba(0,0,0,0.1)'
                  }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                    {state}
                  </Typography>
                </Box>
              ))}
            </Box>

          </Paper>
        </Grid>


        {!hideUI && (
          <>
            <Grid item xs={12}>

              <Grid container spacing={2} direction="row">

                <Grid item xs={12} sm={4} md={4.0}>
                  <PieDistribuionEquipament TITLE={"Tipo"} DATA_POINTS={eqTypeDataPiePoins} />
                </Grid>
                <Grid item xs={12} sm={4} md={4.0}>
                  <PieDistribuionEquipament TITLE={"Estado Agora"} DATA_POINTS={actualStatusDataPiePoins} />
                </Grid>

                <Grid item xs={12} sm={4} md={4.0}>
                  <PieDistribuionEquipament TITLE={"Eventos"} DATA_POINTS={eventsDataPiePoins} />
                </Grid>


              </Grid>
            </Grid>


            <Grid item xs={12}>
              <Grid container spacing={2} direction="row">
                {useMaintenanceModule && (
                  <Grid item xs={12} sm={4} md={4}>
                    <PieDistribuionEquipament TITLE={"Disponível / Manuteção"} DATA_POINTS={disponibilidadeDataPiePoins} />
                  </Grid>
                )}

                {useMaintenanceModule && (
                  <Grid item xs={12} sm={4} md={4}>
                    <BarDistributionChart
                      TITLE={"Próxima preventivas"}
                      DATA={[
                        {
                          type: "column",
                          showInLegend: false,
                          name: "Preventivas",
                          dataPoints: Object.entries(preventivasHistogram).map(([label, value]) => ({
                            label,
                            y: value,
                          })),
                        },
                      ]}
                    />
                  </Grid>
                )}

              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2} direction="row">

                <Grid item xs={12} sm={6} md={6}>
                  <LinePerTimeChart TITLE={"Uso diário médio"} MAXY={24} DURATION_HOURS DATA={utilizacaoPerTimeDatapoins} />
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <LinePerTimeChart TITLE={"Total de Eventos por dia"} DATA={eventsPerTimeDatapoins} />
                </Grid>

              </Grid>
            </Grid>

            <Grid item xs={12}>
              {timelineRangePoints.length > 0 ? (
                <HeartbeatTimelineChart
                  fillGaps
                  TITLE="Visão diária — período selecionado"
                  DATA={timelineRangePoints}
                  startDate={timelineStartDate}
                  endDate={timelineEndDate}
                  page={timelinePage}
                  totalPages={Math.ceil(((equipmentsData || []).filter((e: any) => (setor === "TODOS" || e.setor === setor) && (!timelineFilter || (`${e.id} - ${e.apelido}`).toLowerCase().includes(timelineFilter.toLowerCase())))).length / ITEMS_PER_PAGE) || 1}
                  onPageChange={setTimelinePage}
                  filterText={timelineFilter}
                  onFilterChange={(text) => {
                    setTimelineFilter(text);
                    setTimelinePage(1); // Reset page on filter
                  }}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              ) : (
                <Alert severity="info">Não há dados suficientes para montar a linha do tempo das máquinas nesta data ou setor.</Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={5} style={{ padding: '20px' }}>
                <h2>Resumo de Equipamentos (Período selecionado)</h2>
                {equipmentsData && heartbeatsData && (
                  <TableExplorer
                    key={setor || 'todos'}
                    dbName={operation}
                    tableName="equipaments_summary"
                    startDate={startDate}
                    endDate={endDate}
                    visibleColumns={['tid', 'id', 'apelido', 'tipo', 'setor', 'horimetro 30dias antes', 'horimetro na data final', 'horas_ligadas_periodo', 'estado', 'ultimo_heartbeat']}
                    match_filters_dict={(setor && setor !== "TODOS") ? { "id": equipmentsData.map((e: any) => e.id) } : undefined}
                    dataProcessor_cb={(data) => {
                      return data.map((row: any) => {
                        let estado = "Desconhecido";
                        let ultimo_heartbeat = "N/A";
                        const lasthb = heartbeatsData.find((h: any) => h.id === row.id);
                        if (lasthb) {
                          estado = state_i_to_name(lasthb.state);
                          if (lasthb.stamp) {
                            const dateStr = String(lasthb.stamp).includes('GMT') || String(lasthb.stamp).endsWith('Z') ? lasthb.stamp : lasthb.stamp + 'Z';
                            const date = new Date(dateStr);
                            if (!isNaN(date.getTime())) {
                              ultimo_heartbeat = date.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
                            }
                          }
                          const diffSeconds = secondsSinceTelemetryTimestamp(lasthb["stamp"], referenceTime);
                          if (diffSeconds === null || diffSeconds > 60 * 15) {
                            estado = "Desligado";
                          }
                        } else {
                          estado = "Sem dados";
                        }
                        return {
                          ...row,
                          'horimetro 30dias antes': row.horimetro_30_dias_antes != null ? Math.round(Number(row.horimetro_30_dias_antes) / 60) : '—',
                          'horimetro na data final': row.horimetro != null ? Math.round(Number(row.horimetro) / 60) : '—',
                          estado: estado,
                          ultimo_heartbeat: ultimo_heartbeat
                        };
                      });
                    }}
                  />
                )}
              </Paper>
            </Grid>
          </>
        )}

      </Grid>
    </DrawerHeader>
  );
}

